import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ROLES, type Role } from '@/shared/auth';
import type {
  UpdateUserAccessRequest,
  UserAccessResponse,
  UserPrivilegeOverrideEffect,
} from '@/shared/api';
import { useApi } from '@/shared/api';
import { companyKeys } from '@/features/team/queries';
import { useAssignableProperties } from '@/features/team/useAssignableProperties';
import { SCOPED_ACCESS_ROLES, SELECTABLE_ACCESS_ROLES } from './UserAccessScopeEditor';

function overrideMap(access: UserAccessResponse) {
  return Object.fromEntries(
    access.privileges.flatMap((privilege) =>
      privilege.overrideEffect ? [[privilege.id, privilege.overrideEffect]] : [],
    ),
  ) as Record<string, UserPrivilegeOverrideEffect | undefined>;
}

function sameSet(left: string[], right: string[]) {
  return left.length === right.length && left.every((item) => right.includes(item));
}

function initialRole(access: UserAccessResponse): Role {
  const role = access.user.roles.find((candidate) => candidate !== ROLES.PlatformAdmin) ?? '';
  return SELECTABLE_ACCESS_ROLES.includes(role as Role) ? (role as Role) : ROLES.Staff;
}

export function useUserAccessForm(access: UserAccessResponse, open: boolean) {
  const api = useApi();
  const persistedRole = initialRole(access);
  const [role, setRole] = useState<Role>(persistedRole);
  const [companyId, setCompanyId] = useState(access.user.companyId ?? '');
  const [propertyIds, setPropertyIds] = useState(
    access.directProperties.map((property) => property.id),
  );
  const [overrides, setOverrides] = useState(() => overrideMap(access));
  const scoped = SCOPED_ACCESS_ROLES.includes(role);
  const companies = useQuery({
    queryKey: companyKeys.list({ page: 1, pageSize: 200 }),
    queryFn: () => api.companies.list({ page: 1, pageSize: 200 }),
    enabled: open && scoped,
  });
  const properties = useAssignableProperties(open && scoped);

  useEffect(() => {
    if (!open) return;
    setRole(persistedRole);
    setCompanyId(access.user.companyId ?? '');
    setPropertyIds(access.directProperties.map((property) => property.id));
    setOverrides(overrideMap(access));
  }, [access, open, persistedRole]);

  const request = useMemo<UpdateUserAccessRequest>(
    () => ({
      role,
      companyId: scoped && companyId ? companyId : null,
      propertyIds: scoped ? propertyIds : [],
      concurrencyStamp: access.concurrencyStamp,
      privilegeOverrides: Object.entries(overrides).map(([privilegeId, effect]) => ({
        privilegeId,
        effect: effect!,
      })),
      expectedPrivilegeCatalogVersion: access.privilegeCatalogVersion,
      expectedAuthorizationVersion: access.authorizationVersion,
    }),
    [
      access.authorizationVersion,
      access.concurrencyStamp,
      access.privilegeCatalogVersion,
      companyId,
      overrides,
      propertyIds,
      role,
      scoped,
    ],
  );
  const preview = useQuery({
    queryKey: ['users', access.user.id, 'access-preview', request],
    queryFn: () => api.users.previewAccess(access.user.id, request),
    enabled: open,
  });
  const originalOverrides = overrideMap(access);
  const overridesChanged =
    Object.keys(overrides).length !== Object.keys(originalOverrides).length ||
    Object.entries(overrides).some(([id, effect]) => originalOverrides[id] !== effect);
  const changed =
    role !== persistedRole ||
    request.companyId !== (access.user.companyId ?? null) ||
    !sameSet(
      request.propertyIds,
      access.directProperties.map((property) => property.id),
    ) ||
    overridesChanged;

  return {
    role,
    companyId,
    propertyIds,
    overrides,
    scoped,
    request,
    preview,
    companies,
    properties,
    changed,
    setCompanyId,
    resetOverrides: () => setOverrides({}),
    changeRole: (next: Role) => {
      setRole(next);
      if (!SCOPED_ACCESS_ROLES.includes(next)) {
        setCompanyId('');
        setPropertyIds([]);
      }
    },
    toggleProperty: (id: string) =>
      setPropertyIds((current) =>
        current.includes(id) ? current.filter((propertyId) => propertyId !== id) : [...current, id],
      ),
    setOverride: (privilegeId: string, effect: UserPrivilegeOverrideEffect | undefined) =>
      setOverrides((current) => {
        const next = { ...current };
        if (effect) next[privilegeId] = effect;
        else delete next[privilegeId];
        return next;
      }),
  };
}
